/** Notification inbox. */
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { EventSelect } from "@/features/admin/integrations/NotificationRulesPage";
import { CHANNELS, usePreferences, useSetPreference } from "@/features/admin/integrations/queries";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { cn, formatRelative } from "@/shared/lib/format";
import { compactParams, type Paged, usePagedQuery } from "@/shared/lib/paging";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/form";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

/** Notification row. */
export type Notification = Schemas["NotificationOut"];

/** Paged inbox. */
export function useInbox(unreadOnly: boolean, failures = false): Paged<Notification> {
  return usePagedQuery(["notifications", "inbox", unreadOnly, failures], async (cursor) =>
    unwrap(
      await api.GET(
        failures ? "/api/v1/notifications/delivery-failures" : "/api/v1/notifications/",
        {
          params: {
            query: compactParams({ unread_only: unreadOnly || undefined, cursor, limit: 50 }),
          },
        },
      ),
    ),
  );
}

/** Mark notifications read (all when `ids` omitted). */
export function useMarkRead(): UseMutationResult<unknown, Error, string[] | undefined> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids) =>
      unwrap(await api.POST("/api/v1/notifications/mark-read", { body: { ids: ids ?? null } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/**
 * Convert a backend-relative notification URL into an in-app route.
 *
 * The backend emits paths like `/work-orders/<id>`; anything unknown falls
 * back to the raw value so external links still work.
 */
export function routeFor(url: string): string {
  try {
    const u = new URL(url, "http://local");
    return u.pathname + u.search;
  } catch {
    return url || "/";
  }
}

/** Per-member channel preferences (empty channels = mute). */
export function PreferencesPanel(): ReactElement {
  const { t } = useTranslation();
  const prefs = usePreferences();
  const set = useSetPreference();
  const [eventType, setEventType] = useState("*");
  const rows = prefs.data ?? [];
  const current = rows.find((p) => p.event_type === eventType);
  const channels = current?.channels ?? [...CHANNELS];
  const toggle = (ch: string, on: boolean): void =>
    set.mutate({
      event_type: eventType,
      channels: on ? [...channels, ch] : channels.filter((c) => c !== ch),
    });
  if (prefs.isPending) {
    return <LoadingState />;
  }
  if (prefs.isError) {
    return <ErrorState error={prefs.error} onRetry={() => void prefs.refetch()} />;
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("notifications.preferencesHint")}</p>
      <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
        <div className="w-full sm:w-72">
          <label htmlFor="pref-event" className="mb-1 block text-sm font-medium">
            {t("notificationRules.event")}
          </label>
          <EventSelect id="pref-event" value={eventType} onChange={setEventType} allowWildcard />
        </div>
        <div
          className="flex flex-wrap gap-4 pb-2"
          role="group"
          aria-label={t("notificationRules.channels")}
        >
          {CHANNELS.map((ch) => (
            <Checkbox
              key={ch}
              id={`pref-${ch}`}
              label={t(`notificationRules.channelNames.${ch}`)}
              checked={channels.includes(ch)}
              onChange={(e) => toggle(ch, e.target.checked)}
              disabled={set.isPending || !eventType}
            />
          ))}
        </div>
        {channels.length === 0 ? <Badge tone="warning">{t("notifications.muted")}</Badge> : null}
      </div>
      {rows.length ? (
        <ul className="divide-y rounded-md border text-sm">
          {rows.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-center justify-between gap-2 p-2 [overflow-wrap:anywhere]"
            >
              <span className="font-mono text-xs">{p.event_type}</span>
              <span className="flex flex-wrap gap-1">
                {(p.channels ?? []).length ? (
                  (p.channels ?? []).map((c) => (
                    <Badge key={c} tone="outline">
                      {t(`notificationRules.channelNames.${c}`)}
                    </Badge>
                  ))
                ) : (
                  <Badge tone="warning">{t("notifications.muted")}</Badge>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Inbox page. */
export function NotificationsPage(): ReactElement {
  const { t } = useTranslation();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const unreadOnly = !!search.unread;
  const { can } = useAuth();
  const failures = search.tab === "failures" && can("notifications.rule.manage");
  const inbox = useInbox(unreadOnly, failures);
  const markRead = useMarkRead();
  const client = useQueryClient();
  const retryEmail = useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/v1/notifications/{notification_id}/retry-email", {
          params: { path: { notification_id: id } },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <div>
      <PageHeader
        title={t("notifications.title")}
        actions={
          <>
            <Checkbox
              id="unread-only"
              label={t("notifications.unreadOnly")}
              checked={unreadOnly}
              onChange={(e) =>
                void navigate({
                  to: "/notifications",
                  search: { ...search, unread: e.target.checked || undefined },
                })
              }
            />
            {retryEmail.isError ? <ErrorState error={retryEmail.error} /> : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => markRead.mutate(undefined)}
              disabled={markRead.isPending}
            >
              {t("notifications.markAllRead")}
            </Button>
          </>
        }
      />
      <Tabs
        value={search.tab ?? "inbox"}
        onValueChange={(tab) => void navigate({ to: "/notifications", search: { ...search, tab } })}
      >
        <TabsList>
          <TabsTrigger value="inbox">{t("notifications.inbox")}</TabsTrigger>
          <TabsTrigger value="preferences">{t("notifications.preferences")}</TabsTrigger>
          {can("notifications.rule.manage") ? (
            <TabsTrigger value="failures">{t("notifications.deliveryFailures")}</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value={failures ? "failures" : "inbox"}>
          {inbox.query.isPending ? (
            <LoadingState />
          ) : inbox.query.isError ? (
            <ErrorState error={inbox.query.error} onRetry={() => void inbox.query.refetch()} />
          ) : inbox.items.length === 0 ? (
            <EmptyState message={t("notifications.empty")} />
          ) : (
            <ul className="divide-y rounded-md border">
              {inbox.items.map((n) => (
                <li
                  key={n.id}
                  className={cn("flex items-start gap-3 p-3", !n.read_at && "bg-primary/5")}
                >
                  <span
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      n.read_at ? "bg-transparent" : "bg-primary",
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                    <Link
                      to={routeFor(n.url)}
                      className="font-medium hover:underline"
                      onClick={() => {
                        if (!n.read_at) {
                          markRead.mutate([n.id]);
                        }
                      }}
                    >
                      {n.title}
                    </Link>
                    {n.body ? <p className="text-sm text-muted-foreground">{n.body}</p> : null}
                    <p className="text-xs text-muted-foreground">
                      {n.event_type} · {formatRelative(n.created_at)}
                    </p>
                    {n.email_status ? (
                      <p className="text-xs text-muted-foreground">
                        {t(`notifications.delivery.${n.email_status}`)}
                      </p>
                    ) : null}
                    {n.email_status === "failed" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retryEmail.mutate(n.id)}
                        disabled={retryEmail.isPending}
                      >
                        {t("notifications.retryEmail")}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
              {inbox.hasMore ? (
                <li className="p-3 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={inbox.loadMore}
                    disabled={inbox.isLoadingMore}
                  >
                    {t("app.loadMore")}
                  </Button>
                </li>
              ) : null}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="preferences">
          <PreferencesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
